import { api } from './api/client'
import type { Prediction, GameweekResult, GameweekDetail, QuestId, QuestPickState } from './types'
import type { QuestResult } from './questResults'

export type ThemePreference = 'system' | 'light' | 'dark'

/** Must match the inline theme script in index.html, which reads this same
 * key before React mounts to avoid a flash of the wrong theme. */
export const THEME_STORAGE_KEY = 'rwbet:theme-preference'

/** Structural (not lib.dom's `Storage`) so this file type-checks under the
 * server tsconfig too, which has no DOM lib. */
interface MinimalStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function getLocalStorage(): MinimalStorage | undefined {
  return (globalThis as { localStorage?: MinimalStorage }).localStorage
}

/**
 * Theme preference is read synchronously before first paint (see
 * index.html), so it's persisted via localStorage rather than the api/*
 * round trip the rest of this file uses — an async fetch would apply too
 * late and flash the wrong theme.
 */
export function loadThemePreference(): ThemePreference {
  try {
    const stored = getLocalStorage()?.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
  } catch {
    // localStorage unavailable (e.g. private browsing) — fall back to system
  }
  return 'system'
}

export function saveThemePreference(preference: ThemePreference): void {
  try {
    getLocalStorage()?.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // ignore — theme just won't persist this session
  }
}

/**
 * The one-shot "your account was deleted" flag.
 *
 * A self-serve deletion ends in a reload to the sign-in screen, and a bare reload gives no
 * feedback at all — the user is left staring at a login form wondering whether it worked. Set
 * before the reload, read once by `LoginScreen`, and cleared in the same breath so the message
 * does not greet them again next week.
 */
const ACCOUNT_DELETED_KEY = 'rwbet:account-deleted'

export function markAccountDeleted(): void {
  try {
    getLocalStorage()?.setItem(ACCOUNT_DELETED_KEY, '1')
  } catch {
    // ignore — the worst case is a silent reload, which is what happened before this existed
  }
}

/** Reads the flag and clears it: true at most once per deletion. */
export function takeAccountDeletedFlag(): boolean {
  try {
    const storage = getLocalStorage()
    if (storage?.getItem(ACCOUNT_DELETED_KEY) !== '1') return false
    storage.removeItem(ACCOUNT_DELETED_KEY)
    return true
  } catch {
    return false
  }
}

/** Prefix for the skill meter's "last seen" correct-outcome snapshot, suffixed per-profile —
 * `UserProfile` has no stable id, so the
 * profile's display name is the key. Compared on mount against the current
 * correct-outcome %/tier to decide whether the meter's one-time celebratory
 * pulse should play. */
const SKILL_METER_SEEN_KEY_PREFIX = 'rwbet:skill-meter-seen:'

export interface SkillMeterSeenState {
  pct: number
  tierIndex: number
  /** Scored gameweeks the meter last rendered as complete —
   * what the calibration track animates FROM, so a newly-scored gameweek
   * fills its segment instead of appearing already filled. Absent on state
   * written. */
  calibrationProgress?: number
  /** Whether the one-time unlock reveal has already run for this profile.
   * */
  unlockPlayed?: boolean
}

export function loadSkillMeterSeenState(profileKey: string): SkillMeterSeenState | null {
  try {
    const raw = getLocalStorage()?.getItem(SKILL_METER_SEEN_KEY_PREFIX + profileKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<SkillMeterSeenState>
    if (typeof parsed.pct === 'number' && typeof parsed.tierIndex === 'number') {
      return {
        pct: parsed.pct,
        tierIndex: parsed.tierIndex,
        ...(typeof parsed.calibrationProgress === 'number'
          ? { calibrationProgress: parsed.calibrationProgress }
          : {}),
        ...(typeof parsed.unlockPlayed === 'boolean'
          ? { unlockPlayed: parsed.unlockPlayed }
          : {}),
      }
    }
  } catch {
    // localStorage unavailable or value corrupt — treat as "never seen"
  }
  return null
}

export function saveSkillMeterSeenState(
  profileKey: string,
  state: SkillMeterSeenState
): void {
  try {
    getLocalStorage()?.setItem(
      SKILL_METER_SEEN_KEY_PREFIX + profileKey,
      JSON.stringify(state)
    )
  } catch {
    // ignore — the celebratory pulse just won't be tracked this session
  }
}

// Avatar preset identity moved to `avatarPresets.ts` and,
// the *selection* is persisted server-side (PUT /me/avatar) rather than in
// this device's localStorage — so another player's profile can read the
// avatar they actually chose. Re-exported here for existing import sites.
export { AVATAR_PRESET_IDS, type AvatarPresetId } from './avatarPresets'

export function loadPredictions(gameweek: number): Promise<Record<string, Prediction>> {
  return api.get(`/predictions/${gameweek}`)
}

export async function savePredictions(
  gameweek: number,
  predictions: Record<string, Prediction>
): Promise<void> {
  await api.put(`/predictions/${gameweek}`, predictions)
}

/** GameIds whose full-time reveal has already played for this user in the
 * given gameweek. Loaded alongside predictions/results so
 * GameweekGrid can partition settled fixtures into seen vs unseen on mount. */
export function loadRevealSeen(gameweek: number): Promise<string[]> {
  return api.get(`/reveal-seen/${gameweek}`)
}

/** Additive (server insert-or-ignore) — marks one or more fixtures as
 * revealed. Fire-and-forget; never block a reveal animation on the round trip. */
export async function saveRevealSeen(gameweek: number, gameIds: string[]): Promise<void> {
  await api.put(`/reveal-seen/${gameweek}`, { gameIds })
}

/** Dev tool: clears this user's reveal-seen flags for one gameweek so the
 * full-time reveal sequence can be re-triggered on reload. */
export async function clearRevealSeen(gameweek: number): Promise<void> {
  await api.delete(`/reveal-seen/${gameweek}`)
}

/** Weekly quest picks — follows the exact same
 * load-on-gameweek-change / save-on-change pattern as `loadPredictions`/
 * `savePredictions` above. */
export function loadQuestPicks(
  gameweek: number
): Promise<Partial<Record<QuestId, QuestPickState>>> {
  return api.get(`/quest-picks/${gameweek}`)
}

export async function saveQuestPicks(
  gameweek: number,
  picks: Partial<Record<QuestId, QuestPickState>>
): Promise<void> {
  await api.put(`/quest-picks/${gameweek}`, picks)
}

/** The gameweek's graded quest truth — app-wide, so the same call serves the
 * player's own reveal and another player's profile. A quest missing from the response has not
 * been graded yet: its card stays sealed rather than showing a verdict. */
export function loadQuestResults(
  gameweek: number
): Promise<Partial<Record<QuestId, QuestResult>>> {
  return api.get(`/quest-picks/${gameweek}/results`)
}

/** Whether the user has started this gameweek — set the moment
 * the "Predict Now" hero is tapped, so the Weekly Quests section stays
 * visible even if the user backs out of the fixture picker with zero picks
 * made. Presence-of-row on the server, so there's no "unset" call — a fresh
 * gameweek simply hasn't got a row yet. */
export function loadHasStartedGw(gameweek: number): Promise<boolean> {
  return api.get(`/gameweek-started/${gameweek}`)
}

export async function saveHasStartedGw(gameweek: number): Promise<void> {
  await api.put(`/gameweek-started/${gameweek}`, {})
}

export function loadDoubleUpUsedGameweeks(): Promise<number[]> {
  return api.get('/double-up')
}

export async function saveDoubleUpUsedGameweeks(usedGameweeks: number[]): Promise<void> {
  await api.put('/double-up', usedGameweeks)
}

export function loadCleanSweepUsedGameweeks(): Promise<number[]> {
  return api.get('/clean-sweep')
}

export async function saveCleanSweepUsedGameweeks(usedGameweeks: number[]): Promise<void> {
  await api.put('/clean-sweep', usedGameweeks)
}

export function loadGoalfestUsedGameweeks(): Promise<number[]> {
  return api.get('/goalfest')
}

export async function saveGoalfestUsedGameweeks(usedGameweeks: number[]): Promise<void> {
  await api.put('/goalfest', usedGameweeks)
}

export async function loadBankerGameId(gameweek: number): Promise<string | null> {
  const { gameId } = await api.get<{ gameId: string | null }>(`/banker/${gameweek}`)
  return gameId
}

export async function saveBankerGameId(gameweek: number, gameId: string | null): Promise<void> {
  await api.put(`/banker/${gameweek}`, { gameId })
}

/**
 * Read-only, both of them. The server scores continuously and owns these rows;
 * the matching `saveSeasonHistory` / `saveSeasonDetails` are gone along with the `PUT` routes they
 * called, because a client holding a copy from before a fixture finished would otherwise overwrite
 * the server's newer scores with it.
 */
export function loadSeasonHistory(): Promise<GameweekResult[]> {
  return api.get('/season-history')
}

export function loadSeasonDetails(): Promise<Record<number, GameweekDetail>> {
  return api.get('/season-details')
}

/** Which gameweek this user is on. No kickoff anchor rides along: kickoffs are real, and
 *  identical for everyone. */
export interface CurrentGameweekState {
  gameweek: number
}

export function loadCurrentGameweek(
  defaultGw: number
): Promise<CurrentGameweekState> {
  return api.get<CurrentGameweekState>(`/current-gameweek?default=${defaultGw}`)
}

export async function saveCurrentGameweek(gameweek: number): Promise<CurrentGameweekState> {
  return api.put('/current-gameweek', { gameweek })
}

/** The app-wide gameweek phase — the shared "clock" every client reads and polls
 * so the admin's dev actions (deadline / reveal results / finalize) surface for all users. */
export interface GameweekPhase {
  gameweek: number
  deadlinePassed: boolean
  resultsRevealed: boolean
  /** Opaque token that changes whenever the server rescores this gameweek. */
  scoredDigest?: string | null
}

export function loadGameweekPhases(): Promise<GameweekPhase[]> {
  return api.get('/gameweek-phase')
}

/**
 * The open gameweek's real average and highest score, plus who is holding the highest. Computed
 * server-side from every participant's `season_history` row, so it is the same population and the
 * same points the league tables are built from.
 *
 * The identity is null before the gameweek locks (the server refuses it) and whenever nobody has
 * been scored yet.
 */
export interface GameweekStats {
  average: number
  highest: number
  highestUserId: string | null
  highestDisplayName: string | null
}

export function loadGameweekStats(gameweek: number): Promise<GameweekStats> {
  return api.get(`/gameweek/${gameweek}/stats`)
}
